import {
  AlertCircle,
  Bot,
  CircleStop,
  Database,
  History,
  MessageSquarePlus,
  PanelRightClose,
  PanelRightOpen,
  RotateCcw,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  ApiError,
  goldQueriesApi,
  historyApi,
  sourcesApi,
  type GovernedSource,
  type ThreadSummary,
} from "../../services/api";
import {
  streamAgentTurn,
  type AgentIntent,
  type ArtifactComposedEvent,
  type ClarifyingQuestionEvent,
  type FollowUpSuggestion,
} from "../../services/agui";
import { ComposedDashboard } from "../../components/ComposedDashboard";
import { useAutoGrowTextarea } from "../../hooks/useAutoGrowTextarea";
import { ArtifactView } from "../../components/ArtifactView";
import { Markdown } from "../../components/Markdown";
import ArtifactCard from "./ArtifactCard";
import GoldQueryCard from "./GoldQueryCard";
import AnalystHero, { HERO_ACTIONS, type HeroAction } from "./AnalystHero";
import DashboardSummary from "./DashboardSummary";
import FollowUps from "./FollowUps";
import QuestionCard, { SKIP_TOKEN } from "./QuestionCard";
import { AnswerContextStrip, AnswerReceiptPanel, NewTopicButton } from "./conversation";
import { AnswerEmailButton, planEmails } from "../email";
import type { AnswerContext } from "../../services/answerContext";
import type { AnswerReceipt } from "../../services/answerReceipt";
import { devTrace } from "../../services/devTrace";
import { answerContextStep } from "../../services/traceReplay";
import {
  dashboardsFromEvents,
  runIdsOf,
  tracesFromEvents,
} from "../../services/traceReplay";
import { storage, type SelectedContext } from "../../services/storage";
import type { ChatMessage, DevTrace, PageId } from "../../types";

interface AIAnalystPageProps {
  onNavigate: (page: PageId) => void;
  /** Live selection owned by App and shared with the Data Sources page. */
  selectedContext?: SelectedContext | null;
  onSelectContext?: (context: SelectedContext) => void;
  /** Named from the selected source's data domain; absent means unclassified. */
  analystName?: string;
  /**
   * Whether the page is the one on screen.
   *
   * It stays mounted while the user is elsewhere so the conversation survives,
   * which means a turn can stream, and a dashboard can be composed, against a
   * page that has no layout to scroll.
   */
  active?: boolean;
}

const greeting: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "I am DMI AI Analyst. I answer from your governed semantic layer, so I only use certified metrics and dimensions.",
};

// The entry step, and the safe default for an open question. The runtime also
// reads the message: a free-form request to chart or graph something is routed
// to the dashboard network even though the step says CHECK_SOURCE, so this need
// not guess the user's intent.
const ENTRY_STEP = "CHECK_SOURCE";
/** Options a turn can carry beyond its text and step. */
interface TurnOptions {
  /** Only ever set by the user confirming a move to a different contract. */
  contextOverride?: { contextId: string; contextVersion: string };
  /** Which entry action opened the turn. Defaults to a plain question. */
  intent?: AgentIntent;
  /**
   * What to show in the transcript instead of the raw message.
   *
   * A skip is sent as a sentinel the runtime understands, which would read as
   * gibberish in a chat bubble.
   */
  display?: string;
}

function user(content: string): ChatMessage {
  return { id: crypto.randomUUID(), role: "user", content };
}

/**
 * The layer name the runtime stamps on every step that reached the governed
 * semantic layer. Matched rather than inferred from the tool id, so a new
 * governed tool is highlighted without this file changing.
 */
const BCL_LAYER = "Business Context Layer";

function isGovernedStep(trace: DevTrace): boolean {
  return (trace.layer ?? "").includes(BCL_LAYER);
}

/** The statements a governed step generated, if it reported any. */
function governedSql(trace: DevTrace): string[] {
  const value = (trace.detail as Record<string, unknown> | undefined)?.sourceSql;
  if (typeof value === "string") return value.trim() ? [value] : [];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && !!item.trim());
}

/** The rest of the detail, so SQL is not printed twice in two formats. */
function withoutSql(detail: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...detail };
  delete rest.sourceSql;
  return rest;
}

export function AIAnalystPage({
  onNavigate,
  active = true,
  selectedContext,
  onSelectContext,
  analystName,
}: AIAnalystPageProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([greeting]);
  const [input, setInput] = useState("");
  // Explicitly a string: a thread opened from history is an arbitrary id, not
  // the template-literal type crypto.randomUUID() would otherwise infer.
  const [threadId, setThreadId] = useState<string>(() => crypto.randomUUID());
  // Which runs the trace panel shows, and the only thing that needs to
  // remember a run id: a live turn is one run, but a conversation reopened
  // from history has as many runs as it had turns, and the panel is meant to
  // tell the story of the conversation rather than of the latest turn.
  const [traceRunIds, setTraceRunIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [traceOpen, setTraceOpen] = useState(true);
  const [traces, setTraces] = useState<DevTrace[]>(devTrace.list());
  const [followUps, setFollowUps] = useState<FollowUpSuggestion[]>([]);
  // Applies to the next turn only: the user setting the earlier answers aside
  // for one question, not switching conversation memory off.
  const [newTopic, setNewTopic] = useState(false);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [historyError, setHistoryError] = useState("");
  // The local copy keeps this component usable on its own (including tests).
  // In the app, selectedContext is controlled by App so a choice made on the
  // Data Sources page reaches this already-mounted component immediately.
  const [localContext, setLocalContext] = useState<SelectedContext | null>(() =>
    storage.selectedContext(),
  );
  const activeContext =
    selectedContext === undefined ? localContext : selectedContext;

  /** Pins a governed context for the rest of the conversation. */
  const pinContext = useCallback((next: SelectedContext) => {
    storage.saveSelectedContext(next);
    setLocalContext(next);
    onSelectContext?.(next);
  }, [onSelectContext]);

  // The request the current turn is answering, kept so that choosing a data
  // source can retry it. Asking which source to use is a detour, not a new
  // request: the thing the user actually asked for has to survive it, along
  // with the intent that told the runtime it was a dashboard build.
  const pendingRequestRef = useRef<{
    question: string;
    workflowStep: string;
    intent?: AgentIntent;
  } | null>(null);

  // The dashboard the view should move to, and whether this turn produced one.
  // See the scroll effect below.
  const dashboardAnchorRef = useRef<string>("");
  const composedThisTurnRef = useRef(false);
  // The document being read, if any. Held here rather than on the message so
  // that closing it returns the reader to the conversation without discarding
  // the card that opened it, and so a revision can replace what is on screen.
  const [openArtifact, setOpenArtifact] = useState<ArtifactComposedEvent | null>(
    null,
  );
  // Set when the runtime refuses because the pinned contract moved or differs.
  // Holds the retry the user can confirm, rather than retrying silently.
  const [contextPrompt, setContextPrompt] = useState<{
    message: string;
    contextId: string;
    contextVersion: string;
    question: string;
  } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The composer looks like a single field, so the space around the text has to
  // behave like one. Held so a click on the padding or beside the send button
  // can put the cursor where the user plainly meant it to go.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(composerRef, input);

  // Read from the context layer rather than local state. The panel used to
  // report "connection required" from a mock while the analyst was answering
  // questions from that very source.
  const [connected, setConnected] = useState<GovernedSource[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    sourcesApi
      .list()
      .then((items) => {
        if (!cancelled) {
          setConnected(items.filter((item) => item.status === "connected"));
        }
      })
      .catch(() => {
        if (!cancelled) setConnected([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The Email control appears only under replies to a request to email, and the
  // agent's own words about emailing are not shown next to it.
  const emailPlans = useMemo(
    () =>
      planEmails(
        messages.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          plain: !(message.dashboard || message.artifact || message.goldQuery || message.card),
        })),
      ),
    [messages],
  );

  // The name of the context answering this conversation. Resolved against the
  // live list rather than read straight from storage, so a pinned context that
  // has since been renamed or withdrawn does not keep showing a stale label.
  const sourceLabel = useMemo(() => {
    if (connected === null) return "Checking the context layer…";
    if (activeContext) {
      const live = connected.find(
        (item) => item.id === activeContext.contextId,
      );
      return live?.name ?? activeContext.name;
    }
    const [only] = connected;
    if (connected.length === 1 && only) return only.name;
    return connected.length
      ? "Select a semantic model"
      : "No semantic model available";
  }, [connected, activeContext]);

  // Conversations live in the agent runtime's store, so the list survives a
  // refresh and a new browser. It is reloaded after each completed turn, which
  // is when a brand new thread first becomes visible.
  const refreshThreads = useCallback(() => {
    historyApi
      .threads()
      .then(setThreads)
      .catch(() => setHistoryError("Conversation history is unavailable."));
  }, []);

  useEffect(refreshThreads, [refreshThreads]);

  const openThread = useCallback(async (id: string) => {
    try {
      const stored = await historyApi.messages(id);
      abortRef.current?.abort();
      setThreadId(id);
      setHistoryError("");
      setFollowUps([]);
      setNewTopic(false);
      setMessages([
        greeting,
        ...stored.map((item) => ({
          id: item.messageId,
          role: item.role === "user" ? ("user" as const) : ("assistant" as const),
          content: item.content,
        })),
      ]);
      // The trace is rebuilt from the runtime's stored events rather than kept
      // in the browser, so a conversation opened in a new session still shows
      // how it was answered. A failure here loses the trace, not the
      // conversation, so it must not fail the open.
      setTraceRunIds([]);
      try {
        const events = await historyApi.events(id);
        devTrace.replaceRuns(tracesFromEvents(events));
        setTraceRunIds(runIdsOf(events));
        // The same events carry the dashboards this conversation produced, which
        // used to be lost on open because they were page state. Appended after
        // the transcript: the message store gives no run boundary to interleave
        // them against, and appending keeps later turns below them.
        const restored = dashboardsFromEvents(events);
        if (restored.length) {
          setMessages((current) => [
            ...current,
            ...restored.map((dashboard) => ({
              id: `dashboard-${dashboard.dashboardId}`,
              role: "assistant" as const,
              content: "",
              dashboard,
            })),
          ]);
        }
      } catch {
        setHistoryError("The reasoning trace for that conversation is unavailable.");
      }
    } catch {
      setHistoryError("That conversation could not be loaded.");
    }
  }, []);

  useEffect(() => devTrace.subscribe(setTraces), []);

  /**
   * Keeps the view on whatever the turn produced.
   *
   * Scrolling to the end of the transcript is right for an answer and wrong for
   * a dashboard. A dashboard is appended where it was produced, which is before
   * the agent's closing remark and the follow-up suggestions, so scrolling past
   * it left the reader looking at a sentence telling them their dashboard was
   * ready somewhere else while it sat off-screen above.
   */
  useEffect(() => {
    // A hidden page has no layout, so scrolling it does nothing while still
    // consuming the anchor. Left for the return trip instead, which is when a
    // dashboard composed while the user was away needs bringing into view.
    if (!active) return;
    const anchor = dashboardAnchorRef.current;
    if (anchor) {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        dashboardAnchorRef.current = "";
        return;
      }
    }
    // Once a dashboard has been shown, the rest of the turn does not steal the
    // view back. Cleared when the next turn starts.
    if (composedThisTurnRef.current) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy, active]);
  useEffect(() => () => abortRef.current?.abort(), []);

  /** Appends a delta to the streaming reply, creating it on first arrival. */
  function appendDelta(replyId: string, delta: string) {
    setMessages((current) => {
      const existing = current.find((message) => message.id === replyId);
      if (!existing) {
        return [
          ...current,
          { id: replyId, role: "assistant" as const, content: delta },
        ];
      }
      return current.map((message) =>
        message.id === replyId
          ? { ...message, content: message.content + delta }
          : message,
      );
    });
  }

  /**
   * Attaches how the question was understood to the streaming reply.
   *
   * The runtime reports it before the analyst starts writing, so the reply may
   * not exist yet; it is created empty and the text then streams into it.
   */
  function attachAnswerContext(replyId: string, answerContext: AnswerContext) {
    setMessages((current) => {
      if (!current.some((message) => message.id === replyId)) {
        return [
          ...current,
          { id: replyId, role: "assistant" as const, content: "", answerContext },
        ];
      }
      return current.map((message) =>
        message.id === replyId ? { ...message, answerContext } : message,
      );
    });
  }

  /**
   * Attaches the answer's receipt to its reply. Sent once the text is final,
   * so the reply normally exists; a turn whose text never arrived still keeps
   * the receipt, because a warning about figures must not be lost with it.
   */
  function attachAnswerReceipt(replyId: string, answerReceipt: AnswerReceipt) {
    setMessages((current) => {
      if (!current.some((message) => message.id === replyId)) {
        return [
          ...current,
          { id: replyId, role: "assistant" as const, content: "", answerReceipt },
        ];
      }
      return current.map((message) =>
        message.id === replyId ? { ...message, answerReceipt } : message,
      );
    });
  }

  async function respond(
    value: string,
    workflowStep: string = ENTRY_STEP,
    options: TurnOptions = {},
  ) {
    const question = value.trim();
    if (!question || busy) return;
    const { contextOverride, intent, display } = options;
    const startsTopic = newTopic;

    setInput("");
    setNewTopic(false);
    setBusy(true);
    setContextPrompt(null);
    // A new turn supersedes the previous turn's suggestions, whether it came
    // from one of them or from the composer.
    setFollowUps([]);
    setMessages((current) => [...current, user(display ?? question)]);

    // Recorded before the turn starts, so a clarifying question that interrupts
    // it can hand the same request back afterwards.
    pendingRequestRef.current = { question, workflowStep, intent };
    // A new turn may scroll freely again until it composes something.
    composedThisTurnRef.current = false;

    const turnRunId = crypto.randomUUID();
    // Appended, not replaced: a new turn in a reopened conversation should
    // continue its trace rather than hide the turns that came before.
    setTraceRunIds((current) => [...current, turnRunId]);
    const replyId = crypto.randomUUID();
    const controller = new AbortController();
    abortRef.current = controller;
    let received = false;

    devTrace.record({
      runId: turnRunId,
      step: "agent_turn_started",
      actor: "AI Analyst",
      summary: "Sent the question to the governed agent network.",
      inputs: { question, workflowStep },
    });

    try {
      await streamAgentTurn(
        {
          threadId,
          runId: turnRunId,
          message: question,
          workflowStep,
          // An override only ever comes from the user confirming a move to a
          // different contract, which is the one case a thread may be repinned.
          contextId: contextOverride?.contextId ?? activeContext?.contextId,
          contextVersion:
            contextOverride?.contextVersion ?? activeContext?.contextVersion,
          repinContext: Boolean(contextOverride),
          intent,
          relationHint: startsTopic ? "new" : undefined,
        },
        {
          onText: (delta) => {
            received = true;
            appendDelta(replyId, delta);
          },
          onTrace: (summary, actor) => {
            devTrace.record({
              runId: turnRunId,
              step: "agent_activity",
              actor,
              level: "success",
              summary,
            });
          },
          onStep: (step) => {
            const detail = step.detail ?? {};
            devTrace.record({
              runId: turnRunId,
              step: step.phase,
              actor: step.actor,
              level:
                step.status === "error"
                  ? "error"
                  : step.status === "success"
                    ? "success"
                    : "info",
              summary: step.summary,
              phase: step.phase,
              layer: step.layer,
              service: step.service,
              tool: step.tool,
              code: typeof detail.code === "string" ? detail.code : undefined,
              detail,
              durationMs:
                typeof detail.durationMs === "number"
                  ? detail.durationMs
                  : undefined,
            });
          },
          onQuestion: (question) => {
            // Counts as a reply: a turn that ends in a question has done its
            // job, so the "finished without an answer" fallback must not fire.
            received = true;
            setMessages((current) => [
              ...current,
              {
                id: crypto.randomUUID(),
                role: "assistant",
                content: "",
                card: question,
              },
            ]);
            devTrace.record({
              runId: turnRunId,
              step: "clarifying_question",
              actor: "Dashboard Coordinator",
              level: "info",
              summary: `Asked: ${question.summary}`,
              output: {
                questionId: question.questionId,
                answerStep: question.answerStep,
                options: question.options.length,
              },
            });
          },
          onFollowUps: (suggestions) => {
            setFollowUps(suggestions);
          },
          // Not counted as a reply: it describes an answer, and the answer
          // itself still has to arrive for the turn to have produced one.
          onAnswerContext: (context) => {
            attachAnswerContext(replyId, context);
            devTrace.record({
              runId: turnRunId,
              ...answerContextStep(context),
            });
          },
          onAnswerReceipt: (receipt) => {
            attachAnswerReceipt(replyId, receipt);
            devTrace.record({
              runId: turnRunId,
              step: "answer_checked",
              actor: "Answer check",
              level: receipt.status === "unverified" ? "warning" : "success",
              summary: receipt.summary,
              output: {
                checked: receipt.checked,
                verified: receipt.verified,
                flagged: receipt.flagged,
                queries: receipt.receipts.length,
              },
            });
          },
          onSourceReadiness: (ready) => {
            devTrace.record({
              runId: turnRunId,
              step: "source_readiness",
              actor: "AI Analyst",
              level: ready ? "success" : "warning",
              summary: ready
                ? "A governed data source is pinned to this conversation."
                : "No governed data source is selected yet.",
            });
          },
          onDashboardComposed: (event) => {
            // Appended to the transcript so it stays where it was produced.
            // Held as page state it sat below every message, so the next
            // question appeared above it and a second dashboard replaced it.
            const messageId = crypto.randomUUID();
            dashboardAnchorRef.current = `dashboard-anchor-${messageId}`;
            composedThisTurnRef.current = true;
            setMessages((current) => [
              ...current,
              {
                id: messageId,
                role: "assistant",
                content: "",
                dashboard: event,
              },
            ]);
            devTrace.record({
              runId: turnRunId,
              step: "dashboard_composed",
              actor: "Dashboard Composer",
              level: event.saved ? "success" : "warning",
              summary: event.saved
                ? `Composed "${event.title}" with ${event.spec.panels.length} ` +
                  `panels and saved it to the workspace.`
                : `Composed "${event.title}" but it could not be saved.`,
              output: {
                dashboardId: event.dashboardId,
                panels: event.spec.panels.length,
                saved: event.saved,
              },
            });
          },
          onArtifactComposed: (event) => {
            // Appended to the transcript as a card, and opened straight away.
            // A document is what the user asked for, so making them click to
            // see it would be a step for its own sake -- but the card stays so
            // the conversation still records that it was produced.
            const messageId = crypto.randomUUID();
            composedThisTurnRef.current = true;
            setMessages((current) => [
              ...current,
              {
                id: messageId,
                role: "assistant",
                content: "",
                artifact: event,
              },
            ]);
            setOpenArtifact(event);
            devTrace.record({
              runId: turnRunId,
              step: event.revisionOf ? "artifact_revised" : "artifact_composed",
              actor: "Artifact Composer",
              level: event.saved ? "success" : "warning",
              summary: event.saved
                ? `${event.revisionOf ? "Revised" : "Composed"} "${event.title}" ` +
                  `as version ${event.version} with ` +
                  `${event.spec.sections.length} sections.`
                : `Composed "${event.title}" but it could not be saved.`,
              output: {
                artifactId: event.artifactId,
                version: event.version,
                sections: event.spec.sections.length,
                saved: event.saved,
              },
            });
          },
          onGoldQueryOffered: (offer) => {
            // Appended as its own card so it sits beneath the figure it is about.
            // Not opened, not modal, and dismissible: this is an invitation, and
            // interrupting somebody mid-analysis to ask about governance is how
            // an invitation becomes an irritation.
            setMessages((current) => [
              ...current,
              {
                id: crypto.randomUUID(),
                role: "assistant",
                content: "",
                goldQuery: offer,
                // Kept so accepting later can be traced against the turn that
                // produced the figure, which is the run it belongs to.
                traceIds: [turnRunId],
              },
            ]);
            devTrace.record({
              runId: turnRunId,
              step: "gold_query_offered",
              actor: "Execute Query",
              level: "info",
              summary:
                "Offered this answer as a gold query for the governed context.",
              output: {
                contextId: offer.contextId,
                metrics: offer.semanticQuery.metrics,
              },
            });
          },
          onError: (code, message, details) => {
            received = true;
            devTrace.record({
              runId: turnRunId,
              step: "agent_error",
              actor: "AI Analyst",
              level: "error",
              summary: `The agent reported ${code}.`,
              code,
              output: { code },
              detail: details,
            });
            // A contract that moved or disagrees is a decision, not a dead end:
            // offer the move and let the user confirm it.
            if (code === "context_version_unavailable") {
              const current = String(details?.currentVersion ?? "");
              const contextId = String(
                details?.contextId ?? activeContext?.contextId ?? "",
              );
              if (current && contextId) {
                setContextPrompt({
                  message,
                  contextId,
                  contextVersion: current,
                  question,
                });
              }
            } else if (code === "context_pin_conflict") {
              const contextId = String(details?.requestedContextId ?? "");
              const version = String(details?.requestedContextVersion ?? "");
              if (contextId) {
                setContextPrompt({
                  message,
                  contextId,
                  contextVersion: version,
                  question,
                });
              }
            }
            appendDelta(replyId, message);
          },
        },
        controller.signal,
      );

      if (!received) {
        appendDelta(
          replyId,
          "The analyst finished without producing an answer. Please rephrase the question.",
        );
      }
    } catch (error) {
      const failure =
        error instanceof ApiError
          ? error
          : new ApiError(0, {
              code: "unexpected_error",
              message: "The analyst could not complete this request.",
            });
      devTrace.record({
        runId: turnRunId,
        step: "agent_error",
        actor: "AI Analyst",
        level: "error",
        summary: `The request failed with ${failure.code}.`,
        output: { code: failure.code, requestId: failure.requestId },
      });
      appendDelta(
        replyId,
        failure.requestId
          ? `${failure.message} Reference: ${failure.requestId}`
          : failure.message,
      );
    } finally {
      abortRef.current = null;
      setBusy(false);
      // The runtime writes the turn's messages as it goes, so the list is
      // reloaded afterwards to pick up a thread created by this very turn.
      refreshThreads();
    }
  }

  /**
   * Records an answer against its card and sends it as the next turn.
   *
   * The card is marked answered before the turn starts so it cannot be submitted
   * twice, and the step travels with the answer so the runtime can reject one
   * that belongs to a question the thread has already passed.
   */
  function answerQuestion(
    messageId: string,
    question: ClarifyingQuestionEvent,
    answer: string,
  ) {
    if (busy) return;
    setMessages((current) =>
      current.map((message) =>
        message.id === messageId ? { ...message, cardAnswer: answer } : message,
      ),
    );

    // Choosing a governed source is not an interview answer: nothing runs until
    // the thread is pinned, so the selection is applied and the original request
    // is retried against it.
    if (question.questionId === "data_source") {
      const chosen = question.options.find((option) => option.label === answer);
      const contextId = chosen?.id ?? answer;
      // Version is left empty on purpose: the relay resolves the current version
      // for a context, so pinning a stale one here would invite a conflict.
      pinContext({
        contextId,
        contextVersion: "",
        name: chosen?.label ?? contextId,
      });
      // The request that triggered the question, not the word "Continue". Sent
      // literally, "Continue" carries no request and no intent, so the runtime
      // could not tell a dashboard build from a passing remark and answered by
      // asking for a question — which is where the Build Dashboard interview
      // stopped, before it ever reached the composer.
      const original = pendingRequestRef.current;
      void respond(original?.question ?? "Continue", original?.workflowStep ?? ENTRY_STEP, {
        contextOverride: { contextId, contextVersion: "" },
        intent: original?.intent ?? "dashboard",
        display: `Use ${answer}`,
      });
      return;
    }

    void respond(answer, question.answerStep, {
      intent: "dashboard",
      display: answer === SKIP_TOKEN ? "Skipped" : answer,
    });
  }

  /** Removes a dashboard from the transcript, leaving the rest of it intact. */
  function dismissDashboard(messageId: string) {
    setMessages((current) =>
      current.filter((message) => message.id !== messageId),
    );
  }

  /**
   * Keeps an answer as a gold query on the governed contract.
   *
   * The wording is the user's; the query and the figure are not. The API stamps
   * the approver from the session and the context layer re-runs the query, so
   * what gets certified is the governed answer rather than what this page is
   * showing. Failures are re-thrown for the card to report, because the user
   * needs to know their acceptance did not take.
   */
  async function acceptGoldQuery(messageId: string, question: string) {
    const message = messages.find((item) => item.id === messageId);
    const offer = message?.goldQuery;
    if (!offer) return;
    await goldQueriesApi.accept({
      contextId: offer.contextId,
      contextVersion: offer.contextVersion,
      question,
      semanticQuery: offer.semanticQuery,
    });
    setMessages((current) =>
      current.map((item) =>
        item.id === messageId ? { ...item, goldQueryAccepted: true } : item,
      ),
    );
    devTrace.record({
      // Attributed to the turn that produced the figure, so the acceptance sits
      // with the answer it certifies rather than on its own.
      runId: message?.traceIds?.[0] ?? crypto.randomUUID(),
      step: "gold_query_accepted",
      actor: "You",
      level: "success",
      summary: `Kept "${question}" as a gold query for ${offer.contextId}.`,
      output: { contextId: offer.contextId, question },
    });
  }

  function startHeroAction(action: HeroAction) {
    void respond(action.prompt, action.workflowStep ?? ENTRY_STEP, {
      intent: action.intent,
    });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void respond(input);
  }

  function stop() {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
  }

  const followingConversation = messages.some((message) => message.answerContext);

  function reset() {
    abortRef.current?.abort();
    abortRef.current = null;
    const nextRun = crypto.randomUUID();
    // A new thread, so the agent does not carry the previous conversation.
    setThreadId(crypto.randomUUID());
    setTraceRunIds([nextRun]);
    setMessages([greeting]);
    setInput("");
    setNewTopic(false);
    setBusy(false);
    // The document belonged to the conversation being cleared. Leaving it open
    // over an empty transcript would strand it with nothing that produced it.
    setOpenArtifact(null);
    devTrace.record({
      runId: nextRun,
      step: "conversation_reset",
      actor: "AI Analyst",
      summary: "Started a new conversation.",
    });
  }

  return (
    <>
      {/* Hidden rather than unmounted while a document is open, for the same
          reason the whole page is on navigation: the conversation, its trace and
          a turn still streaming all have to survive being read away from. */}
      <div
        className={`analyst-layout ${traceOpen ? "" : "trace-closed"}`}
        hidden={openArtifact !== null}
      >
      <section
        className={`analyst-panel ${messages.length === 1 ? "idle" : ""}`}
      >
        <div className="analyst-head">
          <div>
            <span className="agent-avatar"><Sparkles size={18} /></span>
            <span><strong>DMI AI Analyst</strong></span>
          </div>
          <div className="analyst-actions">
            <label className="history-picker">
              <History size={14} />
              <span className="sr-only">Recent conversations</span>
              <select
                value={threads.some((item) => item.threadId === threadId) ? threadId : ""}
                onChange={(event) => {
                  if (event.target.value) void openThread(event.target.value);
                }}
                aria-label="Recent conversations"
              >
                <option value="">
                  {historyError || (threads.length ? "Recent conversations" : "No saved conversations")}
                </option>
                {threads.map((item) => (
                  <option key={item.threadId} value={item.threadId}>
                    {item.preview || "Untitled conversation"}
                    {/* Only an administrator is sent an owner, and only then
                        because the list is no longer all their own. */}
                    {item.owner ? ` — ${item.owner}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="button tertiary" onClick={reset}>
              <MessageSquarePlus size={16} />
              New chat
            </button>
            {/* The only way to bring the trace back once hidden, so it carries
                a tooltip rather than relying on the icon alone. */}
            <button
              type="button"
              className="icon-button"
              onClick={() => setTraceOpen((value) => !value)}
              title={
                traceOpen
                  ? "Hide the reasoning trace"
                  : "Show the reasoning trace"
              }
              aria-label={
                traceOpen
                  ? "Hide the reasoning trace"
                  : "Show the reasoning trace"
              }
            >
              {traceOpen ? <PanelRightClose size={19} /> : <PanelRightOpen size={19} />}
            </button>
          </div>
        </div>

        <div className="chat-scroll" aria-live="polite">
          {messages.map((message, index) =>
            message.dashboard ? (
              <div key={message.id} id={`dashboard-anchor-${message.id}`}>
                {/* The table explains what was built before the charts are
                    read, which is the order the storyboard presents them in. */}
                <DashboardSummary event={message.dashboard} />
                <ComposedDashboard
                  event={message.dashboard}
                  onDismiss={() => dismissDashboard(message.id)}
                  onOpenWorkspace={() => onNavigate("my-dashboards")}
                />
              </div>
            ) : message.artifact ? (
              <ArtifactCard
                key={message.id}
                event={message.artifact}
                onOpen={() => setOpenArtifact(message.artifact ?? null)}
              />
            ) : message.goldQuery ? (
              <GoldQueryCard
                key={message.id}
                offer={message.goldQuery}
                accepted={message.goldQueryAccepted}
                onAccept={(question) => acceptGoldQuery(message.id, question)}
              />
            ) : message.card ? (
              <QuestionCard
                key={message.id}
                question={message.card}
                answer={message.cardAnswer}
                busy={busy}
                onAnswer={(question, answer) =>
                  answerQuestion(message.id, question, answer)
                }
              />
            ) : (
              <article className={`chat-message ${message.role}`} key={message.id}>
                {message.role === "assistant" && <span className="message-avatar"><Bot size={17} /></span>}
                <div className="message-content">
                  <span className="message-role">{message.role === "assistant" ? "DMI AI Analyst" : "You"}</span>
                  {/* Only the analyst's own words. A question typed with
                      asterisks in it is text, and reformatting it would put
                      words the user did not write into their own message. */}
                  {message.role === "assistant" ? (
                    <Markdown text={emailPlans.get(message.id)?.text ?? message.content} />
                  ) : (
                    <p>{message.content}</p>
                  )}
                  {message.answerContext && (
                    <AnswerContextStrip context={message.answerContext} />
                  )}
                  {message.answerReceipt && (
                    <AnswerReceiptPanel receipt={message.answerReceipt} />
                  )}
                  {/* Not while the reply is still streaming: an email of half
                      an answer is a different answer. */}
                  {message.role === "assistant"
                    && !(busy && index === messages.length - 1) && (
                      <AnswerEmailButton
                        traceId={message.id}
                        draft={emailPlans.get(message.id)?.email}
                        context={activeContext}
                      />
                    )}
                </div>
              </article>
            ),
          )}
          {busy && (
            <article className="chat-message assistant">
              <span className="message-avatar"><Bot size={17} /></span>
              <div className="message-content thinking">
                <i /><i /><i />
                <span>Consulting the governed semantic layer…</span>
              </div>
            </article>
          )}
          {contextPrompt && (
            <div className="inline-notice context-prompt" role="alert">
              <AlertCircle size={17} aria-hidden="true" />
              <span>
                {contextPrompt.message}{" "}
                Moving this conversation changes the contract its earlier answers
                came from.
              </span>
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  // Pinned, not just sent: moving version is a decision about
                  // the conversation, so it has to outlive the retry turn.
                  pinContext({
                    contextId: contextPrompt.contextId,
                    contextVersion: contextPrompt.contextVersion,
                    name: activeContext?.name ?? contextPrompt.contextId,
                  });
                  void respond(contextPrompt.question, ENTRY_STEP, {
                    contextOverride: {
                      contextId: contextPrompt.contextId,
                      contextVersion: contextPrompt.contextVersion,
                    },
                  });
                }}
              >
                Move to {contextPrompt.contextVersion || "the current version"}
              </button>
              <button
                type="button"
                className="button tertiary"
                onClick={() => setContextPrompt(null)}
              >
                Keep as is
              </button>
            </div>
          )}
          {!busy && (
            <FollowUps
              suggestions={followUps}
              busy={busy}
              onSelect={(suggestion) =>
                void respond(suggestion.prompt, suggestion.workflowStep || ENTRY_STEP)
              }
            />
          )}
          <div ref={endRef} />
        </div>

        <div className="composer-area">
          {/* Only before the first turn. The actions sit below the composer and
              are always available, so the hero carries the greeting alone. */}
          {messages.length === 1 && <AnalystHero name={analystName} />}
          <form
            className="chat-composer"
            onSubmit={submit}
            // Anywhere in the box starts typing. Guarded twice rather than
            // focusing unconditionally: a click already inside the text must keep
            // placing the caret, and one on a button must reach the button --
            // preventDefault does both of those jobs if it is allowed to run.
            onMouseDown={(event) => {
              const target = event.target as HTMLElement;
              if (target === composerRef.current || target.closest("button")) {
                return;
              }
              event.preventDefault();
              composerRef.current?.focus();
            }}
          >
            <textarea
              ref={composerRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (input.trim()) void respond(input);
                }
              }}
              rows={2}
              placeholder="Ask AI Analyst"
              disabled={busy}
              aria-label="Message DMI AI Analyst"
            />
            <div>
              {/* Both states share one round icon button so the composer does
                  not resize as a turn starts and finishes. The label moves to
                  the accessible name rather than disappearing. */}
              {busy ? (
                <button
                  type="button"
                  className="button danger composer-action"
                  onClick={stop}
                  title="Stop generating"
                  aria-label="Stop generating"
                >
                  <CircleStop size={18} />
                </button>
              ) : (
                <button
                  type="submit"
                  className="button primary composer-action"
                  disabled={!input.trim()}
                  title="Send message"
                  aria-label="Send message"
                >
                  <Send size={17} />
                </button>
              )}
            </div>
          </form>
          {/* Only once an answer has shown the analyst following along. With
              conversation memory off no answer carries a context, so the
              composer is exactly as it was. */}
          {followingConversation && (
            <div className="composer-tools">
              <NewTopicButton
                active={newTopic}
                disabled={busy}
                onToggle={() => setNewTopic((value) => !value)}
              />
            </div>
          )}
          {/* The same four actions as the hero, kept available once the
              conversation has started. Each carries its own intent, so Build
              Dashboard opens the interview and a typed message does not. */}
          <div className="analyst-quick-actions" aria-label="AI Analyst actions">
            {HERO_ACTIONS.map((action) => (
              <button
                key={action.id}
                type="button"
                disabled={busy}
                onClick={() => startHeroAction(action)}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {traceOpen && (
        <aside className="trace-panel">
          {/* Hide rather than clear. The trace is rebuilt from the runtime's
              stored events when a conversation is reopened and repopulated by
              the next turn, so clearing it achieved nothing lasting; getting
              it out of the way is the thing worth offering. This drives the
              same state as the header toggle, which is how it comes back. */}
          <div className="trace-head">
            <div><strong>Agent reasoning trace</strong><small>Task summary, actions taken, and results</small></div>
            <button
              type="button"
              className="icon-button"
              onClick={() => setTraceOpen(false)}
              title="Hide the trace panel"
              aria-label="Hide the trace panel"
            >
              <PanelRightClose size={16} />
            </button>
          </div>
          <div className="trace-console">
            {/* One console rather than a stack of cards. Every step is on the
                page at once, so nothing is behind a click; emphasis and a rule
                between steps carry the structure instead of a border box. */}
            {/* Oldest first: the panel tells the story of the turn in the order
                it happened, which is the opposite of the store's newest-first. */}
            {traces
              .filter((trace) => traceRunIds.includes(trace.runId))
              .slice()
              .reverse()
              .map((trace, index) => (
              <section
                key={trace.id}
                className={`trace-step ${trace.level}${
                  isGovernedStep(trace) ? " governed" : ""
                }`}
              >
                {isGovernedStep(trace) && (
                  // Called out because this is the step that makes an answer
                  // certified rather than merely plausible: the figures came
                  // from the governed layer, not from the model.
                  <p className="trace-bcl">
                    <ShieldCheck size={13} aria-hidden="true" />
                    Invoking BCL (Business Context Layer)
                  </p>
                )}
                <p className="trace-step-head">
                  <span className="trace-seq">{index + 1}</span>
                  <strong>{trace.summary}</strong>
                </p>
                <p className="trace-step-meta">
                  {trace.actor}
                  {trace.phase ? ` · ${trace.phase}` : ` · ${trace.step}`}
                  {trace.layer ? ` · ${trace.layer}` : ""}
                  {typeof trace.durationMs === "number" ? ` · ${trace.durationMs} ms` : ""}
                </p>
                {trace.rationale && <p><b>Rationale summary:</b> {trace.rationale}</p>}
                {trace.service && (
                  <p><b>Service called:</b> <code>{trace.service}</code></p>
                )}
                {trace.tool && <p><b>Tool:</b> <code>{trace.tool}</code></p>}
                {trace.code && (
                  <p className="trace-code"><b>Code:</b> <code>{trace.code}</code></p>
                )}
                {governedSql(trace).map((statement, position) => (
                  // Shown as SQL rather than buried in the detail dump: the
                  // statement is the evidence that a certified answer came from
                  // a real query against the governed model.
                  <details key={position} className="trace-sql">
                    <summary>
                      Generated SQL
                      {governedSql(trace).length > 1
                        ? ` (${position + 1} of ${governedSql(trace).length})`
                        : ""}
                    </summary>
                    <pre>{statement}</pre>
                  </details>
                ))}
                {trace.detail && Object.keys(withoutSql(trace.detail)).length > 0 && (
                  <pre>{JSON.stringify(withoutSql(trace.detail), null, 2)}</pre>
                )}
                {trace.output && <pre>{JSON.stringify(trace.output, null, 2)}</pre>}
              </section>
            ))}
            {!traces.some((trace) => traceRunIds.includes(trace.runId)) && (
              <div className="empty-state small">
                <RotateCcw size={23} />
                <strong>Trace begins with your first request</strong>
              </div>
            )}
          </div>
          <button
            type="button"
            className="source-readiness"
            onClick={() => onNavigate("data-sources")}
          >
            <Database size={17} />
            <span>
              <strong>Semantic Model</strong>
              <small>{sourceLabel}</small>
            </span>
            <i className={connected?.length ? "ready" : ""} />
          </button>
        </aside>
      )}
      </div>
      {openArtifact && (
        <ArtifactView
          event={openArtifact}
          onClose={() => setOpenArtifact(null)}
        />
      )}
    </>
  );
}
